import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { Box, Chip, Button, Tooltip, Typography } from '@mui/material'
import StarIcon from '@mui/icons-material/Star'
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import ClearAllOutlinedIcon from '@mui/icons-material/ClearAllOutlined'

import { useQuickFilters } from '../../../utils/use-quick-filters'
import SaveQuickFilterDialog from '../SaveQuickFilterDialog'
import ManageQuickFiltersDialog from '../ManageQuickFiltersDialog'

export default function QuickFiltersBar({ pageType }) {
  const { t } = useTranslation()
  const location = useLocation()

  const {
    publicFilters,
    privateFilters,
    defaultFilter,
    activeFilter,
    loading,
    applyFilter,
    clearAllFilters,
    saveCurrent,
    updateFilter,
    deleteFilter,
    setAsDefault,
    clearDefault,
  } = useQuickFilters({ pageType, location })

  const [saveOpen, setSaveOpen] = useState(false)
  const [manageOpen, setManageOpen] = useState(false)

  const allFilters = [...publicFilters, ...privateFilters]

  const renderChip = (filter) => {
    const isActive = activeFilter?.id === filter.id
    const isDefault = defaultFilter?.id === filter.id
    const label = (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
        {isDefault && <StarIcon sx={{ fontSize: 14, flexShrink: 0 }} />}
        <Box
          component="span"
          sx={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {filter.name}
        </Box>
      </Box>
    )
    // The chip face already shows the filter name; on an active chip the
    // tooltip is the deactivation hint by itself, on an inactive chip it's
    // the description (with the name as a fallback for truncated labels).
    const tooltipParts = isActive
      ? [t('quick-filters.click-to-deactivate')]
      : [filter.description, filter.name].filter(Boolean)
    return (
      <Tooltip key={filter.id} title={tooltipParts.join(' — ')}>
        <Chip
          label={label}
          variant={isActive ? 'filled' : 'outlined'}
          color={isActive ? 'primary' : 'default'}
          onClick={() => {
            if (isActive) {
              clearAllFilters()
            } else {
              applyFilter(filter)
            }
          }}
          onDelete={isActive ? () => clearAllFilters() : undefined}
          size="small"
          sx={{
            flexShrink: 0,
            maxWidth: 160,
            height: 24,
            fontSize: 12,
            '& .MuiChip-label': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              px: 1,
            },
            '& .MuiChip-deleteIcon': { fontSize: 14 },
          }}
        />
      </Tooltip>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        alignItems: { xs: 'stretch', lg: 'center' },
        gap: 1,
        py: 1.5,
        mb: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flex: 1,
          minWidth: 0,
          flexWrap: { xs: 'wrap', lg: 'nowrap' },
          overflowX: { xs: 'visible', lg: 'auto' },
          // Symmetric vertical padding so the chip row's visual centre lines
          // up with the action buttons. The scrollbar sits inside the bottom
          // padding when overflow is present.
          py: { xs: 0, lg: 1 },
          pr: { xs: 0, lg: 4 },
          // Soft fade on the right edge so the row dissolves into space when
          // it's scrollable, instead of clipping abruptly. Mask-image fades
          // pixels out regardless of underlying page background colour.
          maskImage: {
            xs: 'none',
            lg: 'linear-gradient(to right, black calc(100% - 32px), transparent)',
          },
          WebkitMaskImage: {
            xs: 'none',
            lg: 'linear-gradient(to right, black calc(100% - 32px), transparent)',
          },
          // Thin, subtle scrollbar so it complements rather than crowds the chips.
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(0,0,0,0.18) transparent',
          '&::-webkit-scrollbar': { height: 6 },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.18)',
            borderRadius: 3,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(0,0,0,0.35)',
          },
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            flexShrink: 0,
            mr: 0.5,
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
          }}
        >
          {t('quick-filters.title')}
        </Typography>
        {!loading && allFilters.length === 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontStyle: 'italic' }}
          >
            {t('quick-filters.empty-state')}
          </Typography>
        )}
        {allFilters.map(renderChip)}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
          flexWrap: 'wrap',
          justifyContent: { xs: 'flex-end', lg: 'flex-start' },
        }}
      >
        {activeFilter && (
          <Button
            size="small"
            variant="text"
            startIcon={<ClearAllOutlinedIcon />}
            onClick={clearAllFilters}
          >
            {t('quick-filters.clear-all')}
          </Button>
        )}

        <Button
          size="small"
          variant="text"
          startIcon={<BookmarkAddOutlinedIcon />}
          onClick={() => setSaveOpen(true)}
        >
          {t('quick-filters.save-current')}
        </Button>

        {allFilters.length > 0 && (
          <Button
            size="small"
            variant="text"
            startIcon={<SettingsOutlinedIcon />}
            onClick={() => setManageOpen(true)}
          >
            {t('quick-filters.manage')}
          </Button>
        )}
      </Box>

      <SaveQuickFilterDialog
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        onSave={saveCurrent}
      />

      <ManageQuickFiltersDialog
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        publicFilters={publicFilters}
        privateFilters={privateFilters}
        defaultFilter={defaultFilter}
        onSetAsDefault={setAsDefault}
        onClearDefault={clearDefault}
        onUpdateFilter={updateFilter}
        onDeleteFilter={deleteFilter}
      />
    </Box>
  )
}
